import React, { useEffect, useMemo, useState } from 'react';
import type { Core } from 'cytoscape';
import type {
  RuntimeToken,
  RuntimeValueBadge,
  SimulationSnapshot,
} from '../types';

interface ExecutionOverlayProps {
  cy: Core | null;
  snapshot: SimulationSnapshot | null;
}

const overlayContainerStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  zIndex: 1002,
};

const toScreenPoint = (cy: Core, x: number, y: number) => {
  const pan = cy.pan();
  const zoom = cy.zoom();
  return {
    x: x * zoom + pan.x,
    y: y * zoom + pan.y,
  };
};

const renderToken = (cy: Core, token: RuntimeToken) => {
  const edge = cy.getElementById(token.edgeId);
  if (!edge || edge.length === 0) return null;

  const source = edge.sourceEndpoint();
  const target = edge.targetEndpoint();
  const x = source.x + (target.x - source.x) * token.progress;
  const y = source.y + (target.y - source.y) * token.progress;
  const point = toScreenPoint(cy, x, y);

  return (
    <div
      key={token.id}
      style={{
        position: 'absolute',
        left: point.x - 6,
        top: point.y - 6,
        width: 12,
        height: 12,
        borderRadius: '50%',
        background: token.phase === 'input' ? '#1890ff' : '#52c41a',
        boxShadow: '0 0 0 4px rgba(24, 144, 255, 0.18), 0 2px 6px rgba(0,0,0,0.18)',
      }}
    />
  );
};

const renderValueBadge = (cy: Core, badge: RuntimeValueBadge) => {
  const node = cy.getElementById(badge.nodeId);
  if (!node || node.length === 0) return null;

  const bb = node.boundingBox({ includeLabels: false });
  const topRight = toScreenPoint(cy, bb.x2, bb.y1);

  return (
    <div
      key={badge.nodeId}
      style={{
        position: 'absolute',
        left: topRight.x + 8,
        top: topRight.y - 10,
        maxWidth: 180,
        padding: '4px 8px',
        borderRadius: 999,
        border: '1px solid rgba(82, 196, 26, 0.35)',
        background: 'rgba(246, 255, 237, 0.96)',
        color: '#237804',
        fontSize: 12,
        lineHeight: 1.3,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      title={badge.displayText}
    >
      {badge.displayText}
    </div>
  );
};

const renderActiveProcessHighlight = (
  cy: Core,
  activeProcessId: string | null
) => {
  if (!activeProcessId) return null;
  const node = cy.getElementById(activeProcessId);
  if (!node || node.length === 0) return null;

  const bb = node.boundingBox({ includeLabels: false });
  const topLeft = toScreenPoint(cy, bb.x1, bb.y1);
  const bottomRight = toScreenPoint(cy, bb.x2, bb.y2);

  return (
    <div
      key={`highlight-${activeProcessId}`}
      style={{
        position: 'absolute',
        left: topLeft.x - 10,
        top: topLeft.y - 10,
        width: bottomRight.x - topLeft.x + 20,
        height: bottomRight.y - topLeft.y + 20,
        borderRadius: 999,
        border: '2px solid rgba(24, 144, 255, 0.7)',
        boxShadow: '0 0 0 8px rgba(24, 144, 255, 0.12)',
        background: 'rgba(24, 144, 255, 0.05)',
      }}
    />
  );
};

const ExecutionOverlay: React.FC<ExecutionOverlayProps> = ({ cy, snapshot }) => {
  const [viewportVersion, setViewportVersion] = useState(0);
  const hasRuntimeVisuals =
    !!snapshot?.activeProcessId ||
    !!snapshot?.tokens.length ||
    !!Object.keys(snapshot?.runtimeValues || {}).length;

  useEffect(() => {
    if (!cy || !hasRuntimeVisuals) return;

    let rafId: number | null = null;
    // 通过 rAF 合并 Cytoscape 频繁的视口事件，避免 overlay 位置抖动。
    const scheduleViewportUpdate = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        setViewportVersion(version => version + 1);
      });
    };

    cy.on('pan zoom resize render position', scheduleViewportUpdate);

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      cy.off('pan zoom resize render position', scheduleViewportUpdate);
    };
  }, [cy, hasRuntimeVisuals]);

  const renderedValues = useMemo(
    () => Object.values(snapshot?.runtimeValues || {}),
    [snapshot, viewportVersion]
  );

  if (!cy || !snapshot) return null;
  if (!hasRuntimeVisuals) {
    return null;
  }

  return (
    <div style={overlayContainerStyle}>
      {renderActiveProcessHighlight(cy, snapshot.activeProcessId)}
      {snapshot.tokens.map(token => renderToken(cy, token))}
      {renderedValues.map(value => renderValueBadge(cy, value))}
    </div>
  );
};

export default ExecutionOverlay;
